import { createContext, useContext, useState } from 'react'

import { AGENT_NAME } from './accountModel'
import { AgentFace } from './agentFaces'

/**
 * What this option calls the agent. A named character and the plain brand are
 * two different pitches, so the name has to vary per option rather than being
 * fixed for the whole prototype.
 */
const AgentNameContext = createContext(AGENT_NAME)

interface FaceState {
  face?: string
  /** Characters the fleet can switch between, where an option offers a choice. */
  options?: AgentFace[]
  choose: (src: string) => void
}

/** The agent's face, where an option has drawn one. Alongside the name because
 *  every surface that says the name also shows the face. */
const AgentFaceContext = createContext<FaceState>({ choose: () => undefined })

/** What this option calls the Artifacts surface, where that is not its name. */
const ArtifactsLabelContext = createContext<string | undefined>(undefined)

export function AgentNameProvider({
  name,
  face,
  faceOptions,
  artifactsLabel,
  children,
}: {
  name: string
  face?: string
  faceOptions?: AgentFace[]
  artifactsLabel?: string
  children: React.ReactNode
}) {
  // Held here rather than in the strip, so every surface under this option —
  // the docked pane, the phone's bar, the handoff mock — changes together.
  const [chosen, setChosen] = useState(face)

  return (
    <AgentNameContext.Provider value={name}>
      <AgentFaceContext.Provider value={{ face: chosen, options: faceOptions, choose: setChosen }}>
        <ArtifactsLabelContext.Provider value={artifactsLabel}>{children}</ArtifactsLabelContext.Provider>
      </AgentFaceContext.Provider>
    </AgentNameContext.Provider>
  )
}

export function useAgentName() {
  return useContext(AgentNameContext)
}

export function useAgentFace() {
  return useContext(AgentFaceContext).face
}

/** The roster and the way to change it, for a surface that lets you pick. */
export function useFacePicker() {
  const { options, choose } = useContext(AgentFaceContext)
  return { options, choose }
}

export function useArtifactsLabel() {
  return useContext(ArtifactsLabelContext)
}
